import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/logo.png';
import api from '../../services/api';

const Footer = () => {
    const currentYear = new Date().getFullYear();

    // Confirmed final — client's helpline number, reformatted for wa.me (no +, spaces, or dashes).
    const WHATSAPP_NUMBER = '919296968551';
    const whatsappPublishUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('I WANT TO PUBLISH MY BOOK/ARTICLES')}`;
    const [queryEmail, setQueryEmail] = useState('');
    const [queryMessage, setQueryMessage] = useState('');
    const [querySubmitting, setQuerySubmitting] = useState(false);
    const [queryError, setQueryError] = useState('');
    const [querySent, setQuerySent] = useState(false);

    const handleQuerySubmit = async (e) => {
        e.preventDefault();
        setQuerySubmitting(true);
        setQueryError('');
        try {
            await api.post('/contact', { email: queryEmail, message: queryMessage });
            setQuerySent(true);
            setQueryEmail('');
            setQueryMessage('');
            setTimeout(() => setQuerySent(false), 5000);
        } catch (error) {
            setQueryError(error.response?.data?.message || 'Failed to send your query. Please try again.');
        } finally {
            setQuerySubmitting(false);
        }
    };

    const footerLinks = {
        company: [
            { name: 'About Us', path: '/about' },
            { name: 'Contact Us', path: '/contact' },
            { name: 'Privacy Policy', path: '/privacy' },
            { name: 'Terms & Conditions', path: '/terms' },
            { name: 'Refund Policy', path: '/refund' },
        ],
        resources: [
            { name: 'University Study Material', path: '/university-study-material' },
            { name: 'Board Practice Sets', path: '/board-practice-sets' },
            { name: 'Mock Tests', path: '/mock-tests-new' },
            { name: 'Leaderboard', path: '/leaderboard' },
        ],
    };

    // Real SVG social media icons
    const socialLinks = [
        { 
            name: 'Facebook', 
            icon: (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/>
                </svg>
            ), 
            url: 'https://facebook.com', 
            color: 'hover:bg-[#1877F2]' 
        },
        { 
            name: 'Twitter', 
            icon: (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.44 4.83c-.8.37-1.5.38-2.22.02.93-.56.98-.96 1.32-2.02-.88.52-1.86.9-2.9 1.1-.82-.88-2-1.43-3.3-1.43-2.5 0-4.55 2.04-4.55 4.54 0 .36.03.7.1 1.04-3.77-.2-7.12-2-9.36-4.75-.4.67-.6 1.45-.6 2.3 0 1.56.8 2.95 2 3.77-.74-.03-1.44-.23-2.05-.57v.06c0 2.2 1.56 4.03 3.64 4.44-.67.2-1.37.2-2.06.08.58 1.8 2.26 3.12 4.25 3.16C5.78 18.1 3.37 18.8 1 18.6c2 1.2 4.27 1.9 6.8 1.9 8.14 0 12.6-6.7 12.6-12.6 0-.2-.01-.4-.02-.6.86-.6 1.6-1.4 2.2-2.2z"/>
                </svg>
            ), 
            url: 'https://twitter.com', 
            color: 'hover:bg-[#1DA1F2]' 
        },
        { 
            name: 'Instagram', 
            icon: (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.98c2.93 0 3.28.01 4.44.06 1.07.05 1.65.23 2.04.38.52.2.88.44 1.27.83.39.39.63.75.83 1.27.15.39.33.97.38 2.04.05 1.16.06 1.51.06 4.44s-.01 3.28-.06 4.44c-.05 1.07-.23 1.65-.38 2.04-.2.52-.44.88-.83 1.27-.39.39-.75.63-1.27.83-.39.15-.97.33-2.04.38-1.16.05-1.51.06-4.44.06s-3.28-.01-4.44-.06c-1.07-.05-1.65-.23-2.04-.38-.52-.2-.88-.44-1.27-.83-.39-.39-.63-.75-.83-1.27-.15-.39-.33-.97-.38-2.04-.05-1.16-.06-1.51-.06-4.44s.01-3.28.06-4.44c.05-1.07.23-1.65.38-2.04.2-.52.44-.88.83-1.27.39-.39.75-.63 1.27-.83.39-.15.97-.33 2.04-.38 1.16-.05 1.51-.06 4.44-.06zM12 7a5 5 0 100 10 5 5 0 000-10zm6.5-.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM12 9a3 3 0 110 6 3 3 0 010-6z"/>
                </svg>
            ), 
            url: 'https://instagram.com', 
            color: 'hover:bg-[#E4405F]' 
        },
        { 
            name: 'YouTube', 
            icon: (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.5 6.2c-.3-1-1-1.8-2-2.1-1.7-.5-8.6-.5-8.6-.5s-6.9 0-8.6.5c-1 .3-1.7 1.1-2 2.1-.5 1.7-.5 5.3-.5 5.3s0 3.6.5 5.3c.3 1 1 1.8 2 2.1 1.7.5 8.6.5 8.6.5s6.9 0 8.6-.5c1-.3 1.7-1.1 2-2.1.5-1.7.5-5.3.5-5.3s0-3.6-.5-5.3zM9.5 15.2V8.8l5.7 3.2-5.7 3.2z"/>
                </svg>
            ), 
            url: 'https://youtube.com', 
            color: 'hover:bg-[#FF0000]' 
        },
        { 
            name: 'Telegram', 
            icon: (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.1-.2-.11-.06-.26-.04-.37-.02-.16.03-2.64 1.68-3.86 2.44-.36.24-.68.36-.97.36-.32 0-.84-.16-1.25-.3-.52-.18-1.04-.45-1.05-.46-.14-.1-.01-.23.11-.32.45-.35 2.6-1.73 4.05-2.82 1.45-1.09 2.8-1.68 3.06-1.78.14-.05.35-.02.46.03.11.05.17.14.19.25.01.05.03.14.02.24-.02.1 0 .15-.01.17z"/>
                </svg>
            ), 
            url: 'https://telegram.org', 
            color: 'hover:bg-[#26A5E4]' 
        },
    ];

    return (
        <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-white mt-16">
            {/* Main Footer */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Desktop: 5 columns grid | Mobile: single column */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-10">
                    
                    {/* Brand Section */}
                    <div className="col-span-1 lg:col-span-2 lg:text-left">
                        {/* Logo in white circle - centered on mobile, left on desktop */}
                        <div className={`flex ${'justify-center lg:justify-start'} mb-4`}>
                            <div className="w-28 h-28 md:w-32 md:h-32 bg-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                                <img 
                                    src={logo} 
                                    alt="Abha Publication" 
                                    className="h-16 w-16 md:h-20 md:w-20 object-contain"
                                />
                            </div>
                        </div>
                        <p className={`text-gray-400 text-sm md:text-base leading-relaxed max-w-md ${'text-center lg:text-left'} mx-auto lg:mx-0`}>
                            India's leading platform for University Study Materials, Mock Tests for government exams and MCQ Practice sets.
                        </p>
                        {/* Social Icons - center on mobile, left on desktop */}
                        <div className={`flex ${'justify-center lg:justify-start'} space-x-3 mt-6`}>
                            {socialLinks.map((social) => (
                                <a
                                    key={social.name}
                                    href={social.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`w-10 h-10 md:w-11 md:h-11 bg-gray-800 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110 hover:shadow-lg ${social.color}`}
                                    aria-label={social.name}
                                >
                                    {social.icon}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Quick Links - center on mobile, left on desktop */}
                    <div className={`${'text-center lg:text-left'}`}>
                        <h4 className="text-lg md:text-xl font-semibold mb-5 text-indigo-400">Quick Links</h4>
                        <ul className="space-y-2">
                            {footerLinks.company.map((link) => (
                                <li key={link.path}>
                                    <Link
                                        to={link.path}
                                        className="text-gray-400 hover:text-white hover:translate-x-1 transition-all duration-200 inline-block text-sm md:text-base"
                                    >
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Resources - center on mobile, left on desktop */}
                    <div className={`${'text-center lg:text-left'}`}>
                        <h4 className="text-lg md:text-xl font-semibold mb-5 text-indigo-400">Resources</h4>
                        <ul className="space-y-2">
                            {footerLinks.resources.map((link) => (
                                <li key={link.path}>
                                    <Link
                                        to={link.path}
                                        className="text-gray-400 hover:text-white hover:translate-x-1 transition-all duration-200 inline-block text-sm md:text-base"
                                    >
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Publish Your Book/Articles - center on mobile, left on desktop */}
                    <div className={`${'text-center lg:text-left'}`}>
                        <h4 className="text-lg md:text-xl font-semibold mb-5 text-indigo-400">Publish Your Book/Articles</h4>
                        <p className="text-gray-400 text-sm md:text-base mb-4">
                            Share your knowledge, publish with us
                        </p>
                        <a
                            href={whatsappPublishUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-500 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.45 9.9-9.9 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 1.67c2.21 0 4.29.86 5.85 2.42a8.23 8.23 0 0 1 2.42 5.83c0 4.55-3.7 8.25-8.27 8.25a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.18 8.18 0 0 1-1.25-4.38c0-4.56 3.71-8.26 8.23-8.26zm-4.7 4.68c-.15 0-.4.06-.61.3-.21.24-.8.78-.8 1.9 0 1.12.82 2.2.94 2.35.11.15 1.6 2.55 3.95 3.47 1.95.77 2.35.62 2.78.58.42-.04 1.36-.55 1.55-1.09.19-.53.19-.98.13-1.08-.06-.1-.21-.16-.44-.28-.24-.12-1.36-.67-1.57-.75-.21-.08-.36-.12-.51.12-.15.24-.59.75-.72.9-.13.15-.27.17-.5.06-.24-.12-1-.37-1.9-1.18-.7-.62-1.18-1.39-1.31-1.63-.14-.24-.02-.36.1-.48.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.15.04-.29-.02-.41-.06-.12-.51-1.26-.71-1.72-.19-.45-.38-.39-.51-.4z"/>
                            </svg>
                            Book Appointment
                        </a>
                    </div>
                </div>

                {/* Newsletter Section */}
                <div className="mt-12 pt-8 border-t border-gray-800">
                    <div className="bg-gradient-to-r from-indigo-900/50 to-purple-900/50 rounded-2xl p-6 md:p-8 backdrop-blur-sm">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                            <div className={`${'text-center lg:text-left'}`}>
                                <div className={`flex ${'justify-center lg:justify-start'} items-center gap-2 mb-2`}>
                                    <span className="text-3xl">💬</span>
                                    <h4 className="text-xl md:text-2xl font-bold text-white">Send Your Query</h4>
                                </div>
                                <p className={`text-gray-400 text-sm md:text-base max-w-md ${'mx-auto lg:mx-0'}`}>
                                    Have a question? Send it here and our team will get back to you.
                                </p>
                            </div>
                            <div>
                                <form onSubmit={handleQuerySubmit} className="flex flex-col gap-3">
                                    <input
                                        type="email"
                                        value={queryEmail}
                                        onChange={(e) => setQueryEmail(e.target.value)}
                                        placeholder="Enter your email address"
                                        className="w-full px-5 py-3 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm md:text-base"
                                        required
                                    />
                                    <textarea
                                        value={queryMessage}
                                        onChange={(e) => setQueryMessage(e.target.value)}
                                        placeholder="Type your query here..."
                                        rows="2"
                                        className="w-full px-5 py-3 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm md:text-base resize-none"
                                        required
                                    />
                                    <button
                                        type="submit"
                                        disabled={querySubmitting}
                                        className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-xl font-semibold transition-all duration-200 whitespace-nowrap shadow-lg hover:shadow-xl transform hover:scale-105 text-sm md:text-base disabled:opacity-50 disabled:hover:scale-100"
                                    >
                                        {querySubmitting ? 'Sending...' : 'Send Query'}
                                    </button>
                                </form>
                                {querySent && (
                                    <div className="mt-3 text-center">
                                        <p className="text-green-400 text-sm md:text-base animate-pulse">✅ Your query has been sent!</p>
                                    </div>
                                )}
                                {queryError && (
                                    <div className="mt-3 text-center">
                                        <p className="text-red-400 text-sm md:text-base">{queryError}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Copyright Bar */}
            <div className="border-t border-gray-800 bg-gray-900/80">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-center">
                        <p className="text-gray-400 text-sm md:text-base">
                            © {currentYear} <span className="text-indigo-400 font-semibold">Abha Publication</span>. All rights reserved.
                        </p>
                        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs md:text-sm">
                            <Link to="/about" className="text-gray-500 hover:text-indigo-400 transition-colors">About</Link>
                            <span className="text-gray-600">|</span>
                            <Link to="/contact" className="text-gray-500 hover:text-indigo-400 transition-colors">Contact</Link>
                            <span className="text-gray-600">|</span>
                            <Link to="/privacy" className="text-gray-500 hover:text-indigo-400 transition-colors">Privacy</Link>
                            <span className="text-gray-600">|</span>
                            <Link to="/terms" className="text-gray-500 hover:text-indigo-400 transition-colors">Terms</Link>
                        </div>
                        <div className="flex flex-wrap justify-center gap-3 text-xs md:text-sm text-gray-500">
                            <span className="flex items-center gap-1">❤️ Made in India</span>
                            <span className="hidden sm:inline">|</span>
                            <span className="flex items-center gap-1">📞 +91-9296968551</span>
                            <span className="hidden sm:inline">|</span>
                            <span className="flex items-center gap-1">✉️ abhapublicationindia@gmail.com</span>
                        </div>
                    </div>
                    {/* Low-key admin panel link — deliberately separate from the main nav rows above */}
                    <div className="text-center mt-3">
                        <a
                            href="https://admin.abhapublication.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-gray-400 text-xs transition-colors"
                        >
                            Admin Login
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;